import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { Hidden } from '@material-ui/core'
import AppBar from '@material-ui/core/AppBar'
import CssBaseline from '@material-ui/core/CssBaseline'
import IconButton from '@material-ui/core/IconButton'
import Toolbar from '@material-ui/core/Toolbar'
import { useTheme } from '@material-ui/core/styles'
import MenuIcon from '@material-ui/icons/Menu'
import clsx from 'clsx'
import { useCallback, useEffect, useState } from 'react'

import IdleWarningOverlay from '../../components/IdleWarningOverlay'
import JobsTableModal from '../../components/Jobs/JobsTableModal'
import SupportFab from '../../components/SupportFab'
import { ENV } from '../../config/env'
import { LOGOUT_MUTATION, ME_QUERY } from '../../graphql/auth'
import { GET_SESSIONS_CONFIGURATION_QUERY } from '../../graphql/iamSessions'
import { redirectToLogin } from '../../helpers/sessionRedirect'
import { SupportScreenProvider } from '../../helpers/supportScreenContext'
import { useIdleWarning } from '../../hooks/useIdleWarning'
import PanelRoutes from '../../routes/panelRoutes'
import { DrawerCostume } from './Drawer'
import TopMenu from './TopMenu'
import useStyles from './styles/index.styles'

export default function MiniDrawer() {
  const classes = useStyles()
  const theme = useTheme()
  const [open, setOpen] = useState(true)
  const [jobsModalIsOpen, setjobsModalIsOpen] = useState(false)

  const { data: sessionConfigData, error: sessionConfigError } = useQuery(
    GET_SESSIONS_CONFIGURATION_QUERY,
    { fetchPolicy: 'cache-and-network' }
  )
  const [pingSession] = useLazyQuery(ME_QUERY, {
    fetchPolicy: 'network-only'
  })

  useEffect(() => {
    if (sessionConfigError) {
      console.error('Failed to load session configuration:', sessionConfigError)
    }
  }, [sessionConfigError])

  const idleTimeoutMinutes =
    sessionConfigData?.sessionConfiguration?.sessionIdleTimeoutMinutes ?? 0
  const warningMinutes = Number(ENV.IDLE_WARNING_MINUTES) || 5

  const [logoutMutation] = useMutation(LOGOUT_MUTATION)

  const handleExpire = useCallback(() => {
    logoutMutation().catch(() => {})
    redirectToLogin({ sessionExpired: true })
  }, [logoutMutation])

  const syncSessionActivity = useCallback(() => {
    pingSession().catch(() => {
      console.error('Failed to refresh session activity')
    })
  }, [pingSession])

  const { showWarning, remainingSeconds, dismissWarning } = useIdleWarning({
    idleTimeoutMs: idleTimeoutMinutes * 60 * 1000,
    warningBeforeMs: warningMinutes * 60 * 1000,
    onExpire: handleExpire,
    onActivity: syncSessionActivity
  })

  const handleDismiss = useCallback(() => {
    dismissWarning()
    syncSessionActivity()
  }, [dismissWarning, syncSessionActivity])

  return (
    <SupportScreenProvider>
      <div className={classes.root}>
        <CssBaseline />
        <AppBar
          position="fixed"
          className={clsx(classes.appBar, {
            [classes.appBarShift]: open
          })}
          color="default">
          <Toolbar className={classes.topMenuContainer}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={() => setOpen(true)}
              edge="start"
              className={clsx(classes.menuButton, {
                [classes.hide]: open
              })}>
              <MenuIcon />
            </IconButton>
            <TopMenu
              open={open}
              {...{
                jobsModalIsOpen,
                setjobsModalIsOpen
              }}
            />
          </Toolbar>
        </AppBar>
        <DrawerCostume open={open} setOpen={setOpen} theme={theme} />
        <main className={classes.content}>
          <div className={classes.toolbar} />
          <PanelRoutes />
        </main>
        {/* Outside <main>, beside the other overlays this shell owns. It is
            fixed-position chrome rather than page content, and a report filed
            from it carries the route the reader was on — so it has to live
            where every authenticated route can reach it, not inside any one
            of them. It hides itself on the support screens. */}
        {/* The action owns its landmark, so hidden actions leave no empty region. */}
        <SupportFab />
      </div>
      <Hidden mdUp>
        <JobsTableModal
          onClose={() => {
            setjobsModalIsOpen(false)
          }}
          isOpen={jobsModalIsOpen}
        />
      </Hidden>
      <IdleWarningOverlay
        open={showWarning}
        remainingSeconds={remainingSeconds}
        onDismiss={handleDismiss}
      />
    </SupportScreenProvider>
  )
}
